import React from 'react';
import { Checkbox, CheckboxRootProps } from '@chakra-ui/react';

/** Control border width shared by every checkbox variant. */
const CHECKBOX_BORDER_WIDTH = '1.5px';

interface BaseCheckboxProps extends Omit<CheckboxRootProps, 'children'> {
	children?: React.ReactNode;
	label?: string;
}

/**
 * Base Checkbox component that wraps Chakra UI's Checkbox with consistent structure
 *
 * Provides a clean API for creating checkbox variants without repeating
 * the Root/HiddenInput/Control/Label structure
 */
export const BaseCheckbox: React.FC<BaseCheckboxProps> = ({ children, label, ...rootProps }) => {
	return (
		<Checkbox.Root {...rootProps}>
			<Checkbox.HiddenInput />
			<Checkbox.Control cursor="pointer" borderWidth={CHECKBOX_BORDER_WIDTH} borderColor="border.strong">
				<Checkbox.Indicator />
			</Checkbox.Control>
			{(children || label) && <Checkbox.Label>{children || label}</Checkbox.Label>}
		</Checkbox.Root>
	);
};

interface StyledCheckboxProps extends BaseCheckboxProps {
	controlProps?: Record<string, any>; // Additional props for Checkbox.Control
}

/**
 * Primary Checkbox with Bundle's primary color scheme
 *
 * Uses primary color for background, border, and check mark when selected
 * White background when unselected with the app's strong border token
 */
export const PrimaryCheckbox: React.FC<StyledCheckboxProps> = ({ controlProps, checked, ...props }) => {
	const defaultControlProps = {
		cursor: 'pointer',
		bg: checked ? 'brand.solid' : 'white',
		color: 'white',
		borderColor: checked ? 'brand.solid' : 'border.strong',
		borderWidth: CHECKBOX_BORDER_WIDTH,
		...controlProps,
	};

	return (
		<Checkbox.Root checked={checked} {...props} cursor="pointer">
			<Checkbox.HiddenInput />
			<Checkbox.Control {...defaultControlProps}>
				<Checkbox.Indicator />
			</Checkbox.Control>
			{(props.children || props.label) && <Checkbox.Label>{props.children || props.label}</Checkbox.Label>}
		</Checkbox.Root>
	);
};

/**
 * Secondary Checkbox with Bundle's secondary color scheme
 *
 * Uses secondary color for background, primary for text, and appropriate borders
 */
export const SecondaryCheckbox: React.FC<StyledCheckboxProps> = ({ controlProps, checked, ...props }) => {
	const defaultControlProps = {
		cursor: 'pointer',
		bg: checked ? 'secondary.fixed' : 'white',
		color: 'brand.fg',
		borderColor: checked ? 'secondary.fixed' : 'border.strong',
		borderWidth: CHECKBOX_BORDER_WIDTH,
		...controlProps,
	};

	return (
		<Checkbox.Root checked={checked} {...props}>
			<Checkbox.HiddenInput />
			<Checkbox.Control {...defaultControlProps}>
				<Checkbox.Indicator />
			</Checkbox.Control>
			{(props.children || props.label) && <Checkbox.Label>{props.children || props.label}</Checkbox.Label>}
		</Checkbox.Root>
	);
};

/**
 * Default export is the BaseCheckbox for basic usage
 */
export default BaseCheckbox;
